'use client';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEnvelopeDispatch } from '../hooks/index.js';
/**
 * Inline memory-suggestion confirmation card (COP-051, MEM-09).
 *
 * Surfaces when the agent's `suggest_memory` tool emits a `data-memory-suggestion`
 * UI part. The Save button posts a `memory_confirm` envelope as a follow-up user
 * chat message; the runtime intercepts it (`tryParseMemoryEnvelope` in
 * `packages/hub/agent/memory/confirm.ts`) and writes the row through the
 * `withUserRole` chokepoint. The Dismiss button posts a `memory_dismiss`
 * envelope; no DB row is written.
 *
 * Renders one of four states keyed off `suggestion.state`:
 *   - 'pending'   — content preview, scope chip, Save / Dismiss buttons
 *   - 'saved'     — terminal "Saved" feedback
 *   - 'dismissed' — terminal "Dismissed" feedback
 *   - 'error'     — terminal error feedback with `errorMessage`
 *
 * Unstyled. Consumers target `.copilot-memory-suggestion-card`,
 * `.copilot-memory-suggestion-card-{content,scope,actions,save,dismiss,state-*}`,
 * `.copilot-memory-suggestion-{previous,origin,header,sources}`, and the
 * `data-state` / `data-suggestion-id` / `data-kind` / `data-origin` attributes.
 */
export function MemorySuggestionCard({ suggestion, children }) {
    // `submitted` disables the Save / Dismiss buttons the instant they are
    // clicked (before the server's data-memory-saved / data-memory-dismissed SSE
    // part upgrades `suggestion.state`), and `dispatchEnvelope` parks the envelope
    // until the chat returns to idle — both owned by `useEnvelopeDispatch`
    // (shared verbatim with the doc-share card; UAT 2026-05-16 follow-ups #2/#3).
    const { dispatchEnvelope, submitted, setSubmitted } = useEnvelopeDispatch();
    const onConfirm = () => {
        if (submitted !== null)
            return;
        setSubmitted('confirming');
        // Defense-in-depth: trackId belongs to scope='track' only. Even if a
        // stale or buggy server emit forwards a trackId on a global-scope
        // suggestion (e.g. the nil UUID surfacing from a hub-only session — see
        // UAT 2026-05-16 test-3), the card refuses to relay it in the confirm
        // envelope. Server-side gate lives in tools.ts; this is the second hop.
        const trackIdForEnvelope = suggestion.scope === 'track' && suggestion.trackId ? suggestion.trackId : undefined;
        dispatchEnvelope(JSON.stringify({
            kind: 'memory_confirm',
            suggestionId: suggestion.suggestionId,
            content: suggestion.content,
            scope: suggestion.scope,
            ...(trackIdForEnvelope !== undefined ? { trackId: trackIdForEnvelope } : {}),
            // IMEM-14 — refine variant: echo the memory this confirm updates. The
            // shared MemoryEnvelopeSchema accepts it (task-02); the server binds the
            // write to the suggestion row (task-03). Absent on plain saves, so the
            // envelope bytes are unchanged for a non-update suggestion.
            ...(suggestion.updatesMemoryId
                ? { updatesMemoryId: suggestion.updatesMemoryId }
                : {}),
        }));
    };
    const onDismiss = () => {
        if (submitted !== null)
            return;
        setSubmitted('dismissing');
        dispatchEnvelope(JSON.stringify({
            kind: 'memory_dismiss',
            suggestionId: suggestion.suggestionId,
        }));
    };
    if (children) {
        return _jsx(_Fragment, { children: children({ suggestion, onConfirm, onDismiss }) });
    }
    // Optimistic in-flight label between click and SSE terminal-state arrival.
    // Once the cross-message map picks up data-memory-saved / data-memory-dismissed,
    // `suggestion.state` flips to a real terminal value and this branch is bypassed.
    const inFlight = submitted !== null && suggestion.state === 'pending';
    // IMEM-14 — refine variant. When the suggestion targets an existing memory,
    // the primary action reads "Update memory" (aria + label) and the memory it
    // replaces renders struck/dimmed above the new content. A plain suggestion
    // (no `updatesMemoryId`) keeps the original "Save" button and no previous
    // block, so its markup is byte-identical to before.
    //
    // DRM-04 / COP-109 — dream-proposal variants (milestone-17 task-07). The
    // server hydration (task-06) MAY stamp `kind` / `origin` / `sourceContents`
    // onto the part; legacy extraction payloads carry none of them, so the
    // effective kind falls back to the IMEM-14 `updatesMemoryId` discriminator:
    //   - 'merge'  — struck source list ("Replaces") + Merge / Merged
    //   - 'add' with sources (abstraction) — unstruck "Derived from" list, Save
    //   - 'forget' — struck content + Remove / Removing… / Removed
    //   - 'update' — the existing refine display; optional sources = "Evidence"
    // The confirm/dismiss envelopes are UNCHANGED for every kind (the server
    // confirm is row-bound — task-05).
    const effectiveKind = suggestion.kind ?? (suggestion.updatesMemoryId !== undefined ? 'update' : 'add');
    const primaryLabel = effectiveKind === 'merge'
        ? 'Merge'
        : effectiveKind === 'forget'
            ? 'Remove'
            : effectiveKind === 'update'
                ? 'Update memory'
                : 'Save';
    const primaryAriaLabel = effectiveKind === 'update' ? 'Update memory' : effectiveKind === 'add' ? 'Save memory' : primaryLabel;
    const headerLabel = effectiveKind === 'merge'
        ? 'Merge memories'
        : effectiveKind === 'forget'
            ? 'Remove stale memory'
            : undefined;
    // Source-list block: struck "Replaces" for merge, unstruck "Derived from"
    // for an abstraction (add + sources), unstruck "Evidence" on an update.
    const sourcesAriaLabel = effectiveKind === 'merge'
        ? 'Replaces'
        : effectiveKind === 'add'
            ? 'Derived from'
            : effectiveKind === 'update'
                ? 'Evidence'
                : undefined;
    const sources = sourcesAriaLabel !== undefined &&
        suggestion.sourceContents !== undefined &&
        suggestion.sourceContents.length > 0
        ? suggestion.sourceContents
        : undefined;
    const savedText = effectiveKind === 'merge' ? 'Merged' : effectiveKind === 'forget' ? 'Removed' : 'Saved';
    const confirmingText = effectiveKind === 'forget' ? 'Removing…' : 'Saving…';
    return (_jsxs("div", { className: "copilot-memory-suggestion-card", "data-memory-suggestion-card": "", "data-suggestion-id": suggestion.suggestionId, "data-state": suggestion.state, "data-kind": effectiveKind, "data-origin": suggestion.origin, role: "region", "aria-label": "Memory suggestion", children: [suggestion.origin === 'dream' && (_jsx("div", { className: "copilot-memory-suggestion-origin", children: "Suggested while you were away" })), headerLabel !== undefined && (_jsx("div", { className: "copilot-memory-suggestion-header", children: headerLabel })), effectiveKind === 'update' && suggestion.previousContent !== undefined && (_jsx("div", { className: "copilot-memory-suggestion-previous", "aria-label": "Replaces", children: suggestion.previousContent })), sources !== undefined && (_jsx("ul", { className: effectiveKind === 'merge'
                    ? 'copilot-memory-suggestion-sources copilot-memory-suggestion-sources-struck'
                    : 'copilot-memory-suggestion-sources', "aria-label": sourcesAriaLabel, children: sources.map((source, i) => (_jsx("li", { className: "copilot-memory-suggestion-sources-item", children: source }, i))) })), _jsx("div", { className: effectiveKind === 'forget'
                    ? 'copilot-memory-suggestion-card-content copilot-memory-suggestion-card-content-struck'
                    : 'copilot-memory-suggestion-card-content', children: suggestion.content }), _jsx("div", { className: "copilot-memory-suggestion-card-scope", children: _jsx("span", { className: "copilot-memory-suggestion-card-scope-chip", "data-scope": suggestion.scope, children: suggestion.scope === 'global' ? 'Global' : 'Track' }) }), suggestion.state === 'pending' && !inFlight && (_jsxs("div", { className: "copilot-memory-suggestion-card-actions", children: [_jsx("button", { type: "button", className: "copilot-memory-suggestion-card-save", onClick: onConfirm, "aria-label": primaryAriaLabel, children: primaryLabel }), _jsx("button", { type: "button", className: "copilot-memory-suggestion-card-dismiss", onClick: onDismiss, "aria-label": "Dismiss memory", children: "Dismiss" })] })), inFlight && (_jsx("div", { className: "copilot-memory-suggestion-card-state-saving", role: "status", children: submitted === 'confirming' ? confirmingText : 'Dismissing…' })), suggestion.state === 'saved' && (_jsx("div", { className: "copilot-memory-suggestion-card-state-saved", role: "status", children: savedText })), suggestion.state === 'dismissed' && (_jsx("div", { className: "copilot-memory-suggestion-card-state-dismissed", role: "status", children: "Dismissed" })), suggestion.state === 'error' && (_jsxs("div", { className: "copilot-memory-suggestion-card-state-error", role: "alert", children: [_jsx("span", { className: "copilot-memory-suggestion-card-state-error-label", children: "Could not save:" }), ' ', _jsx("span", { className: "copilot-memory-suggestion-card-state-error-message", children: suggestion.errorMessage ?? suggestion.errorCode ?? 'Unknown error' })] }))] }));
}
//# sourceMappingURL=memory-suggestion.js.map